import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@clarix/auth-store';
import { memberDirectoryApi } from './directory.api';

/**
 * React Query hooks over the members directory for the signed-in workspace.
 * Slug and current user come from the auth-store singleton, like `org.query`.
 */

/**
 * The signed-in user's own directory row (null when there is none). The list
 * search is a substring match, so the row is picked by exact `user_id`.
 */
export function useMyDirectoryMember(email: string | undefined) {
  const slug = useAuthStore((s) => s.workspaceSlug);
  const me = useAuthStore((s) => s.user?.sub ?? null);
  return useQuery({
    queryKey: ['directory', slug, 'me', me, email],
    queryFn: async () => {
      const page = await memberDirectoryApi.searchWithAccount(slug as string, email as string);
      return page.items.find((m) => m.user_id === me) ?? null;
    },
    enabled: !!slug && me != null && !!email,
  });
}

/** One directory row by membership id. */
export function useDirectoryMember(membershipId: string | null) {
  const slug = useAuthStore((s) => s.workspaceSlug);
  return useQuery({
    queryKey: ['directory', slug, 'member', membershipId],
    queryFn: () => memberDirectoryApi.member(slug as string, membershipId as string),
    enabled: !!slug && !!membershipId,
  });
}

/** A member's manager edges (primary + extras of every relationship type). */
export function useDirectoryManagerEdges(membershipId: string | null) {
  const slug = useAuthStore((s) => s.workspaceSlug);
  return useQuery({
    queryKey: ['directory', slug, 'managers', membershipId],
    queryFn: () => memberDirectoryApi.managerEdges(slug as string, membershipId as string),
    enabled: !!slug && !!membershipId,
  });
}
